import { useEffect, useState } from 'react';
import Drawer from './Drawer.tsx';
import { api, errorMessage } from '../lib/api.ts';
import type { DeckOptions } from '../../shared/deck.ts';

/**
 * Deck-level display options — how this deck presents, everywhere it renders
 * (editor preview, published page, PDF). Backed by the studio-managed
 * `export const options` in src/deck.js, so the choice travels WITH the deck.
 * Grows as more deck-wide toggles arrive; per-slide looks stay in the Edit tab.
 */
export default function DeckOptionsDrawer({
  slug,
  onClose,
  onChanged,
}: {
  slug: string;
  onClose: () => void;
  onChanged?: () => void;
}) {
  // null until loaded.
  const [options, setOptions] = useState<DeckOptions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let stale = false;
    api
      .get<{ options?: DeckOptions }>(`/api/decks/${slug}/options`)
      .then((res) => {
        if (!stale) setOptions(res?.options || {});
      })
      .catch((err) => {
        if (!stale) setError(err?.message || 'Could not load options');
      });
    return () => {
      stale = true;
    };
  }, [slug]);

  async function setOption(patch: Partial<DeckOptions>) {
    setBusy(true);
    setError(null);
    try {
      const res = await api.patch<{ options?: DeckOptions }>(`/api/decks/${slug}/options`, patch);
      setOptions(res?.options || {});
      onChanged?.();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const sectionChrome = options ? options.sectionChrome !== false : true;
  const slideNumbers = options ? options.slideNumbers !== false : true;
  const logo = options ? options.logo !== false : true;

  return (
    <Drawer title="Deck options" onClose={onClose}>
      {error && <p className="error small">{error}</p>}
      {!options && !error && <p className="muted small">Loading…</p>}
      {options && (
        <div className="deck-options">
          <label className="deck-options__row">
            <input
              type="checkbox"
              checked={sectionChrome}
              disabled={busy}
              onChange={(e) => setOption({ sectionChrome: e.target.checked })}
            />
            <span>
              <span className="deck-options__label">Section label, top right</span>
              <span className="deck-options__hint muted small">
                The “Part One · Section name · 03/06” line on every slide.
              </span>
            </span>
          </label>
          <label className="deck-options__row">
            <input
              type="checkbox"
              checked={slideNumbers}
              disabled={busy}
              onChange={(e) => setOption({ slideNumbers: e.target.checked })}
            />
            <span>
              <span className="deck-options__label">Page number, bottom right</span>
              <span className="deck-options__hint muted small">The “01”, “02”… count on every slide.</span>
            </span>
          </label>
          <label className="deck-options__row">
            <input
              type="checkbox"
              checked={logo}
              disabled={busy}
              onChange={(e) => setOption({ logo: e.target.checked })}
            />
            <span>
              <span className="deck-options__label">Logo (top left)</span>
              <span className="deck-options__hint muted small">
                The logo on every slide, covers and dividers included.
              </span>
            </span>
          </label>
          <p className="muted small deck-options__note">
            Options apply everywhere the deck renders: editor, published page and PDF. Already-published decks pick them
            up on the next publish.
          </p>
        </div>
      )}
    </Drawer>
  );
}
